import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './ManagerContractMobile.css';
import { Card, Button, Spinner } from 'react-bootstrap';
import { FaGripVertical } from 'react-icons/fa';
import API_BASE_URL from './config/api';

import DashboardNavbar from './Components/Navbar/DashboardNavbar';
import BlueHeader from './Components/Header/BlueHeader';
import Sidebar from './Components/Sidebar/Sidebar';

function Reports() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [buildingName, setBuildingName] = useState("");
  const [buildingManagerName, setBuildingManagerName] = useState();
  const [buildingPhotoUrl, setBuildingPhotoUrl] = useState("");
  const [maintenanceCycles, setMaintenanceCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState("");
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [hasReportsContract, setHasReportsContract] = useState(null);
  const [hasProjectContract, setHasProjectContract] = useState(false);
  const [hasValveAuditContract, setHasValveAuditContract] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Read the token & buildingId from localStorage
  const token = localStorage.getItem('managerToken');
  const userStr = localStorage.getItem('managerUser') || "";
  let buildingId = "";
  
  if (userStr) {
    try {
      const userObj = JSON.parse(userStr);
      buildingId = userObj.buildingId || "";
    } catch (err) {
      console.error("Error parsing user data:", err);
    }
  }

  // Fetch building data including contracts
  useEffect(() => {
    const fetchBuildingData = async () => {
      if (!buildingId) {
        setIsLoading(false);
        return;
      }
      try {
        const resp = await fetch(
          `${API_BASE_URL}/api/getBuildingById`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ buildingId })
          }
        );
        if (!resp.ok) {
          throw new Error(`Error fetching building data: ${resp.status}`);
        }
        const data = await resp.json();
        setBuildingName(data.name || "Unknown");
        setBuildingManagerName(Object.prototype.hasOwnProperty.call(data, 'manager') ? data.manager || '' : data.managerProfile?.manager || '');
        setBuildingPhotoUrl(data.photo_url || "");
        
        // Check if building has any contracts (reports are available for all buildings with contracts)
        const contracts = data.contracts || [];
        setHasReportsContract(contracts.length > 0);
        setHasProjectContract(contracts.includes('project'));
        setHasValveAuditContract(contracts.includes('valve-audit'));
      } catch (err) {
        console.error("fetchBuildingData error:", err);
        setHasReportsContract(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBuildingData();
  }, [token, buildingId]);

  const handleNavigate = (key) => {
    switch (key) {
      case 'hvac':
        navigate('/');
        break;
      case 'mech':
        navigate('/mechanical-room');
        break;
      case 'dryerVents':
        navigate('/dryer-vents');
        break;
      case 'stackCleaning':
        navigate('/stack-cleaning');
        break;
      case 'waterSavings':
        navigate('/water-savings');
        break;
      case 'reports':
        navigate('/reports');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'valveAudit':
        navigate('/valve-audit');
        break;
      default:
        break;
    }
  };

  const EmptyState = () => (
    <div style={{ width: '90%', margin: '2rem auto 0', backgroundColor: '#f8f9fa' }}>
      <Card className="shadow-lg border-0 rounded-lg">
        <Card.Body className="text-center p-5">
          <div style={{ fontSize: '3rem', color: '#224982', marginBottom: '1.5rem' }}>
            <FaGripVertical />
          </div>
          <Card.Title style={{ 
            fontSize: '2rem', 
            marginBottom: '1.5rem', 
            color: '#224982',
            fontWeight: '600'
          }}>
            Reports & Analytics
          </Card.Title>
          <Card.Text style={{ 
            color: '#596272', 
            marginBottom: '2rem',
            fontSize: '1.1rem',
            lineHeight: '1.6',
            maxWidth: '800px',
            margin: '0 auto 2rem'
          }}>
            Access comprehensive reports and analytics for your building's maintenance operations.
            Get detailed insights, performance metrics, and historical data to make informed decisions
            about your building's maintenance strategy.
          </Card.Text>
          <Button 
            variant="primary" 
            size="lg"
            style={{ 
              backgroundColor: '#224982', 
              border: 'none',
              padding: '0.8rem 2.5rem',
              fontSize: '1.1rem',
              fontWeight: '500',
              borderRadius: '8px',
              transition: 'all 0.2s ease-in-out',
              boxShadow: '0 4px 6px rgba(34, 73, 130, 0.2)'
            }}
            className="hover-effect"
            onClick={() => {
              // This would open your proposal flow
              console.log('Request reports access');
            }}
          >
            Get Started
          </Button>
        </Card.Body>
      </Card>
    </div>
  );

  const LoadingState = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
      <Spinner animation="border" role="status" style={{ color: '#224982' }}>
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );

  return (
    <>
      <Sidebar
        buildingName={buildingName}
        managerName={buildingManagerName}
        activeKey="reports"
        onNavigate={handleNavigate}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        buildingPhotoUrl={buildingPhotoUrl}
        showProjectsTab={hasProjectContract}
        showValveAuditTab={hasValveAuditContract}
      />

      <div
        className="dashboard-content manager-contract-page"
        style={{
          marginLeft: collapsed ? 64 : 220,
          transition: 'margin-left 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          minWidth: 0,
          transform: 'translate3d(0,0,0)',
          willChange: 'margin-left',
          backfaceVisibility: 'hidden',
        }}
      >
        <DashboardNavbar
          logoutInSidebar
          buildingName={buildingName}
          buildingId={buildingId}
        />

        <BlueHeader
          maintenanceCycles={maintenanceCycles}
          selectedCycle={selectedCycle}
          setSelectedCycle={setSelectedCycle}
          selectedMaintenance={selectedMaintenance}
          buildingName={buildingName}
        />

        {isLoading ? (
          <LoadingState />
        ) : !hasReportsContract ? (
          <EmptyState />
        ) : (
          <div className="manager-contract-content" style={{ padding: '2rem' }}>
            {/* Add your reports content here */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2>Reports & Analytics Dashboard</h2>
              <p>Welcome to the Reports & Analytics Dashboard</p>
              {/* Add your reports components here */}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Reports;
